/*
 * Global client-side navigation protection.
 *
 * Protected routes:
 *
 * - /dashboard
 * - /page/*
 *
 * Important:
 *
 * This guard does NOT automatically restore Remember Me sessions.
 *
 * A remembered user who refreshes the browser should first return
 * to the landing page and see the existing Welcome Back dialog.
 *
 * Only after the user explicitly presses Continue should the
 * remembered session be restored.
 *
 * Normal authenticated SPA navigation still works because a live
 * session already has both:
 *
 * - auth.user
 * - auth.auth_token
 */

export default function ({
  app,
  store,
}) {
  if (
    !process.client ||
    !app.router
  ) {
    return
  }

  const isProtectedRoute = (
    route
  ) => {
    const path =
      route &&
      route.path
        ? String(route.path)
        : ''

    return (
      path === '/dashboard' ||
      path.startsWith('/page/')
    )
  }

  const getUser = () => {
    try {
      return (
        store.getters[
          'auth/getUser'
        ] || null
      )
    } catch (_) {
      return null
    }
  }

  const getToken = () => {
    try {
      return (
        store.getters[
          'auth/getToken'
        ] || null
      )
    } catch (_) {
      return null
    }
  }

  const hasAuthenticatedSession =
    () => {
      return Boolean(
        getUser() &&
        getToken()
      )
    }

  const applyBearerToken = () => {
    const token =
      getToken()

    if (
      !token ||
      !app.$axios
    ) {
      return false
    }

    if (
      typeof app.$axios.setToken ===
        'function'
    ) {
      app.$axios.setToken(
        token,
        'Bearer'
      )

      return true
    }

    if (
      typeof app.$axios.setHeader ===
        'function'
    ) {
      app.$axios.setHeader(
        'Authorization',
        `Bearer ${token}`
      )

      return true
    }

    return false
  }

  app.router.beforeEach(
    (
      to,
      from,
      next
    ) => {
      if (!isProtectedRoute(to)) {
        next()
        return
      }

      /*
       * Normal authenticated navigation.
       *
       * The user is already actively logged in, so allow
       * dashboard/page navigation without interruption.
       */
      if (
        hasAuthenticatedSession()
      ) {
        applyBearerToken()

        next()
        return
      }

      /*
       * No live authenticated session exists.
       *
       * Do not:
       *
       * - inspect Remember Me;
       * - call $resumeMyUseSession();
       * - call tryAutoLoginFromPersisted();
       * - perform any login here.
       *
       * Return to the welcome page instead. index.vue will decide
       * whether to show the Welcome Back dialog.
       */
      const redirectTarget =
        to &&
        to.fullPath
          ? to.fullPath
          : null

      next({
        path: '/',
        query:
          redirectTarget &&
          redirectTarget !== '/'
            ? {
                redirect:
                  redirectTarget,
              }
            : undefined,

        replace: true,
      })
    }
  )
}